import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { TbPlus, TbEdit, TbTrash, TbLockOpen, TbLock, TbCheck } from 'react-icons/tb';

import PageHeader from '../../components/misc/PageHeader';
import Button from '../../components/buttons/Button';
import ModalLayout from '../../components/layouts/ModalLayout';
import Input from '../../components/misc/Input';
import Labeled from '../../components/misc/Labeled';
import DatePicker from '../../components/forms/DatePicker';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import { BookYear, CreateBookYearDto, UpdateBookYearDto } from '../../../types/bankAccount';
import * as bankAccountService from '../../../services/community/bankAccountService';

interface BookYearsSettingsPageProps {
  hideHeader?: boolean;
}

const PRESETS = [
  { label: 'Jan 1', month: 0 },
  { label: 'Apr 1', month: 3 },
  { label: 'Jul 1', month: 6 },
  { label: 'Aug 1', month: 7 },
  { label: 'Sep 1', month: 8 },
  { label: 'Oct 1', month: 9 },
];

const formatDate = (date: Date | undefined): string =>
  date ? dayjs(date).format('DD-MM-YYYY') : '—';

export default function BookYearsSettingsPage({
  hideHeader = false,
}: BookYearsSettingsPageProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.bookYear' });

  const [bookYears, setBookYears] = useState<BookYear[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editing, setEditing] = useState<BookYear | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [draft, setDraft] = useState<CreateBookYearDto | UpdateBookYearDto>({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bankAccountService.getBookYears();
      setBookYears(data);
    } catch (error: unknown) {
      const message = (error as Error)?.message ?? String(error);
      toast.error(message);
      setBookYears([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const sorted = useMemo(
    () => [...bookYears].sort((a, b) => dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf()),
    [bookYears],
  );

  const handleCreate = async () => {
    if (!draft.startDate || !draft.endDate) {
      toast.error(t('errors.datesRequired'));
      return;
    }
    try {
      await bankAccountService.createBookYear({
        startDate: draft.startDate,
        endDate: draft.endDate,
        label: (draft as CreateBookYearDto).label ?? null,
      });
      toast.success(t('success.created'));
      setDraft({});
      setIsCreateModalOpen(false);
      refresh();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ??
        (error as Error)?.message ??
        String(error);
      toast.error(message);
    }
  };

  const openEditModal = (year: BookYear) => {
    setEditing(year);
    setDraft({
      startDate: year.startDate,
      endDate: year.endDate,
      label: year.label,
    });
    setIsEditModalOpen(true);
  };

  const handleEdit = async () => {
    if (!editing) return;
    try {
      await bankAccountService.updateBookYear(editing.bookYearId, draft as UpdateBookYearDto);
      toast.success(t('success.updated'));
      setEditing(null);
      setIsEditModalOpen(false);
      setDraft({});
      refresh();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ??
        (error as Error)?.message ??
        String(error);
      toast.error(message);
    }
  };

  const handleDelete = async (year: BookYear) => {
    if (!window.confirm(t('confirmations.delete', { label: year.label ?? formatDate(year.startDate) }))) {
      return;
    }
    try {
      await bankAccountService.deleteBookYear(year.bookYearId);
      toast.success(t('success.deleted'));
      refresh();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ??
        (error as Error)?.message ??
        String(error);
      toast.error(message);
    }
  };

  const handleToggleClose = async (year: BookYear) => {
    try {
      await bankAccountService.updateBookYear(year.bookYearId, { isClosed: !year.isClosed });
      toast.success(year.isClosed ? t('success.reopened') : t('success.closed'));
      refresh();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ??
        (error as Error)?.message ??
        String(error);
      toast.error(message);
    }
  };

  return (
    <>
      {!hideHeader && <PageHeader title={t('title')} subtitle={t('subtitle')} />}

      <div className="space-y-6">
        <div className="flex justify-end">
          <Button
            variant="primary-dark"
            onClick={() => {
              const latestEnd = sorted[0]?.endDate ? dayjs(sorted[0].endDate) : dayjs();
              const defaultStart = latestEnd.add(1, 'day').startOf('day').toDate();
              const defaultEnd = dayjs(defaultStart).add(1, 'year').subtract(1, 'day').endOf('day').toDate();
              const defaultLabel = sorted[0]?.endDate
                ? `Boekjaar ${dayjs(defaultStart).year()}`
                : undefined;
              setDraft({
                startDate: defaultStart,
                endDate: defaultEnd,
                label: defaultLabel,
              });
              setIsCreateModalOpen(true);
            }}>
            <TbPlus />
            {t('addBookYear')}
          </Button>
        </div>

        {sorted.length > 0 && (
          <div className="rounded-lg border border-secondary-200 bg-white p-4">
            <p className="mb-2 text-sm font-medium text-gray-700">{t('timeline')}</p>
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100">
              {sorted.map((year, idx) => {
                const total = sorted.reduce(
                  (acc, y) => acc + Math.max(1, dayjs(y.endDate).diff(dayjs(y.startDate), 'day')),
                  0,
                );
                const span = Math.max(1, dayjs(year.endDate).diff(dayjs(year.startDate), 'day'));
                const width = `${(span / total) * 100}%`;
                return (
                  <div
                    key={year.bookYearId}
                    title={`${formatDate(year.startDate)} → ${formatDate(year.endDate)}`}
                    style={{ width, backgroundColor: idx % 2 === 0 ? '#3B82F6' : '#10B981' }}
                    className="h-full"
                  />
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-gray-500">{t('emptyState')}</p>
          </div>
        ) : (
          <div className="rounded-lg border border-secondary-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    {t('table.label')}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    {t('table.startDate')}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    {t('table.endDate')}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    {t('table.status')}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                    {t('table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sorted.map((year) => (
                  <tr key={year.bookYearId}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {year.label ?? `Boekjaar ${dayjs(year.startDate).year()}`}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatDate(year.startDate)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatDate(year.endDate)}</td>
                    <td className="px-4 py-3 text-sm">
                      {year.isClosed ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                          <TbLock className="h-3 w-3" /> {t('status.closed')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                          <TbCheck className="h-3 w-3" /> {t('status.open')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ActionMenu>
                        <ActionMenuButton
                          icon={TbEdit}
                          text={t('actions.edit')}
                          onClick={() => openEditModal(year)}
                        />
                        <ActionMenuButton
                          icon={year.isClosed ? TbLockOpen : TbLock}
                          text={year.isClosed ? t('actions.reopen') : t('actions.close')}
                          onClick={() => handleToggleClose(year)}
                        />
                        <ActionMenuButton
                          icon={TbTrash}
                          text={t('actions.delete')}
                          className="text-danger"
                          onClick={() => handleDelete(year)}
                        />
                      </ActionMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ModalLayout
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">{t('createModal.title')}</h2>
          <div className="space-y-4">
            <Labeled label={t('createModal.startPreset')}>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((preset) => {
                  const today = dayjs();
                  const targetYear = today.month() < preset.month ? today.year() : today.year() + 1;
                  const targetStart = dayjs(new Date(targetYear, preset.month, 1));
                  const targetEnd = targetStart.add(1, 'year').subtract(1, 'day');
                  return (
                    <button
                      key={preset.label}
                      type="button"
                      className="rounded-md border border-secondary-200 bg-white px-3 py-1 text-sm hover:bg-secondary-50"
                      onClick={() =>
                        setDraft((prev) => ({
                          ...prev,
                          startDate: targetStart.toDate(),
                          endDate: targetEnd.endOf('day').toDate(),
                          label: prev.label ?? `Boekjaar ${targetYear}`,
                        }))
                      }>
                      {preset.label} {targetYear}
                    </button>
                  );
                })}
              </div>
            </Labeled>
            <Labeled label={t('createModal.startDate')}>
              <DatePicker
                date={draft.startDate}
                setDate={(date) =>
                  setDraft((prev) => ({
                    ...prev,
                    startDate: date,
                    endDate: date ? dayjs(date).add(1, 'year').subtract(1, 'day').endOf('day').toDate() : undefined,
                  }))
                }
                includeTime={false}
              />
            </Labeled>
            <Labeled label={t('createModal.endDate')}>
              <DatePicker
                date={draft.endDate}
                setDate={(date) => setDraft((prev) => ({ ...prev, endDate: date }))}
                includeTime={false}
              />
            </Labeled>
            <Input
              id="book-year-label"
              label={t('createModal.label')}
              type="text"
              placeholder={t('createModal.labelPlaceholder')}
              value={(draft as CreateBookYearDto).label ?? ''}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, label: e.target.value }))
              }
            />
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary-dark" onClick={handleCreate}>
                {t('create')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>

      <ModalLayout
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        closeButton
        className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[500px]">
        <div>
          <h2 className="mb-4 text-lg font-semibold">{t('editModal.title')}</h2>
          <div className="space-y-4">
            <Labeled label={t('editModal.startDate')}>
              <DatePicker
                date={draft.startDate}
                setDate={(date) => setDraft((prev) => ({ ...prev, startDate: date }))}
                includeTime={false}
              />
            </Labeled>
            <Labeled label={t('editModal.endDate')}>
              <DatePicker
                date={draft.endDate}
                setDate={(date) => setDraft((prev) => ({ ...prev, endDate: date }))}
                includeTime={false}
              />
            </Labeled>
            <Input
              id="edit-book-year-label"
              label={t('editModal.label')}
              type="text"
              placeholder={t('editModal.labelPlaceholder')}
              value={(draft as UpdateBookYearDto).label ?? ''}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, label: e.target.value }))
              }
            />
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
                {t('cancel')}
              </Button>
              <Button variant="primary-dark" onClick={handleEdit}>
                {t('update')}
              </Button>
            </div>
          </div>
        </div>
      </ModalLayout>
    </>
  );
}